import React, { useCallback, useEffect, useRef, useState } from "react";
import { ShieldAlert } from "lucide-react";
import api from "../lib/api";
import {
  broadcastSessionEvent,
  subscribeSessionEvents,
} from "../lib/sessionSync";
import { useAuth } from "../lib/auth";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const WARNING_AFTER_MS = 13 * 60 * 1000;
const TIMEOUT_AFTER_MS = 15 * 60 * 1000;
const PULSE_THROTTLE_MS = 5000;
const BROADCAST_THROTTLE_MS = 1000;
const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "touchstart",
  "scroll",
];

export default function SessionInactivityGuard() {
  const { user, logout, expireForInactivity } = useAuth();
  const [warningOpen, setWarningOpen] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const lastPulseRef = useRef(0);
  const lastBroadcastRef = useRef(0);
  const expiredRef = useRef(false);

  const recordActivity = useCallback(
    (force = false) => {
      if (!user || document.visibilityState !== "visible" || expiredRef.current)
        return;
      const now = Date.now();
      lastActivityRef.current = now;
      setWarningOpen(false);
      if (force || now - lastBroadcastRef.current >= BROADCAST_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        broadcastSessionEvent({ type: "activity", userId: user.id });
      }
      if (!force && now - lastPulseRef.current < PULSE_THROTTLE_MS) return;
      lastPulseRef.current = now;
      api
        .post("/auth/activity", {}, { __sessionActivity: true })
        .catch(() => {});
    },
    [user],
  );

  useEffect(() => {
    if (!user) return undefined;
    lastActivityRef.current = Date.now();
    lastPulseRef.current = 0;
    lastBroadcastRef.current = 0;
    expiredRef.current = false;
    setWarningOpen(false);

    const onVisible = () => {
      if (document.visibilityState === "visible") recordActivity(true);
    };
    const onEvent = () => recordActivity(false);
    const unsubscribe = subscribeSessionEvents((event) => {
      if (event.userId && event.userId !== user.id) return;
      if (event.type === "activity") {
        lastActivityRef.current = Date.now();
        setWarningOpen(false);
      }
    });
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onEvent, true),
    );
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);

    const clock = window.setInterval(() => {
      if (document.visibilityState !== "visible" || expiredRef.current) return;
      const idleFor = Date.now() - lastActivityRef.current;
      if (idleFor >= TIMEOUT_AFTER_MS) {
        expiredRef.current = true;
        setWarningOpen(false);
        expireForInactivity();
      } else if (idleFor >= WARNING_AFTER_MS) {
        setWarningOpen(true);
      }
    }, 1000);

    return () => {
      window.clearInterval(clock);
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onEvent, true),
      );
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
      unsubscribe();
    };
  }, [expireForInactivity, recordActivity, user]);

  if (!user) return null;

  return (
    <Dialog open={warningOpen} onOpenChange={() => {}}>
      <DialogContent
        className="!rounded-none max-w-md"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        data-testid="inactivity-warning-dialog"
      >
        <DialogHeader>
          <div
            className="mb-3 flex h-9 w-9 items-center justify-center border"
            style={{ borderColor: "var(--border)" }}
          >
            <ShieldAlert size={17} style={{ color: "var(--ink)" }} />
          </div>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            Still there?
          </DialogTitle>
          <DialogDescription>
            To protect your account, this session will end soon unless you
            continue working.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button
            type="button"
            className="btn-ghost"
            onClick={logout}
            data-testid="inactivity-signout-now"
          >
            Sign out now
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => recordActivity(true)}
            data-testid="inactivity-stay-signed-in"
          >
            Stay signed in
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
